import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { adminMe, adminLogin, adminLogout, setToken, clearToken, getToken } from "@/lib/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null); // null = checking, false = anon, object = authed
  const [checked, setChecked] = useState(false);
  const [authError, setAuthError] = useState(false);

  const refresh = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setAdmin(false);
      setAuthError(false);
      setChecked(true);
      return false;
    }
    setChecked(false);
    try {
      const a = await adminMe();
      setAdmin(a);
      setAuthError(false);
      return true;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearToken();
        setAdmin(false);
        setAuthError(false);
      } else {
        // Keep the token on transient network/server failures so a valid session
        // can be checked again when connectivity returns.
        setAdmin(null);
        setAuthError(true);
      }
      return false;
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const invalidate = () => {
      clearToken();
      setAdmin(false);
      setAuthError(false);
      setChecked(true);
    };
    window.addEventListener("aran:admin-session-invalid", invalidate);
    return () => window.removeEventListener("aran:admin-session-invalid", invalidate);
  }, []);

  const login = async (email, password) => {
    const res = await adminLogin(email, password);
    setToken(res.token);
    setAdmin(res.admin);
    setAuthError(false);
    setChecked(true);
    return res.admin;
  };

  const logout = async () => {
    await adminLogout();
    clearToken();
    setAdmin(false);
    setAuthError(false);
    setChecked(true);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, checked, authError, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
